import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { Invoice, OutboundInvoiceRequest, Supplier } from './types.js';

export function useSearchSuppliers(tenantId: string, q: string) {
  const trimmed = q.trim();
  return useQuery<Supplier[]>({
    queryKey: ['tenant-suppliers', tenantId, trimmed],
    queryFn: () =>
      apiFetch<Supplier[]>(
        `/tenants/${encodeURIComponent(tenantId)}/suppliers?limit=20${
          trimmed ? `&q=${encodeURIComponent(trimmed)}` : ''
        }`,
      ),
    enabled: Boolean(tenantId) && trimmed.length >= 2,
    staleTime: 30_000,
  });
}

export function useCreateInvoice() {
  return useMutation<Invoice, Error, OutboundInvoiceRequest>({
    mutationFn: (payload) =>
      apiFetch<Invoice>('/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
  });
}

/**
 * Sums all line subtotals (qty * unitPrice * (1 + vatRate)) and rounds to 2
 * decimals. Used both by the live preview and the BE-parity validation in
 * the review step (BE accepts ±0.02 rounding).
 */
export function sumLines(
  lines: Array<{ quantity: number; unitPrice: number; vatRate: number }>,
): number {
  const raw = lines.reduce((acc, l) => acc + l.quantity * l.unitPrice * (1 + l.vatRate), 0);
  return Math.round(raw * 100) / 100;
}
